import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';

const NextOfKin = () => {
  const { token, user } = useContext(AuthContext);
  const [nextOfKins, setNextOfKins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [currentKin, setCurrentKin] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    relationship: '',
    phone: '',
    address: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Base URL constant
  const BASE_URL = 'https://www.aceswafund.org/welfare/welfare-system';

  // Use useCallback to memoize the fetch function
  const fetchNextOfKin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Add cache-busting timestamp
      const timestamp = new Date().getTime();

      const response = await fetch(`${BASE_URL}/get-next-of-kin?user_id=${user.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      const result = await response.json();

      if (result.status) {
        console.log('Fetched next of kin:', result.data);
        setNextOfKins(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch next of kin');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  // Fetch next of kin on component mount
  useEffect(() => {
    if (user?.id) {
      fetchNextOfKin();
    }
  }, [fetchNextOfKin, user?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddModal = () => {
    // Check if user already has a next of kin
    if (nextOfKins.length > 0) {
      setError('You can only have one next of kin. Please edit or delete the existing one.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setModalMode('add');
    setCurrentKin(null);
    setFormData({
      full_name: '',
      relationship: '',
      phone: '',
      address: ''
    });
    setShowModal(true);
  };

  const openEditModal = (kin) => {
    setModalMode('edit');
    setCurrentKin(kin);
    setFormData({
      full_name: kin.full_name || '',
      relationship: kin.relationship || '',
      phone: kin.phone || '',
      address: kin.address || ''
    });
    setShowModal(true);
  };

  // Optimistic update for adding
  const handleAddOptimistic = (newKin) => {
    setNextOfKins([newKin]);
  };

  // Optimistic update for editing
  const handleEditOptimistic = (updatedKin) => {
    setNextOfKins(prev => prev.map(kin =>
      kin.id === updatedKin.id ? updatedKin : kin
    ));
  };

  // Optimistic update for deleting
  const handleDeleteOptimistic = (id) => {
    setNextOfKins(prev => prev.filter(kin => kin.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Validate required fields
    if (!formData.full_name || !formData.relationship || !formData.phone) {
      setError('Full name, relationship, and phone are required');
      setSubmitting(false);
      return;
    }

    // Create optimistic update data
    const optimisticData = {
      ...formData,
      id: modalMode === 'edit' ? currentKin.id : Date.now(), // Temporary ID for add
      created_at: new Date().toISOString()
    };

    try {
      // Apply optimistic update immediately
      if (modalMode === 'add') {
        handleAddOptimistic(optimisticData);
      } else {
        handleEditOptimistic(optimisticData);
      }

      // Close modal immediately for better UX
      setShowModal(false);
      setSuccess(modalMode === 'add' ? 'Next of kin added successfully!' : 'Next of kin updated successfully!');
      setTimeout(() => setSuccess(null), 3000);

      // Construct URLs with user_id
      const url = modalMode === 'add'
        ? `${BASE_URL}/add-next-of-kin?user_id=${user.id}`
        : `${BASE_URL}/update-next-of-kin?user_id=${user.id}`;

      const requestBody = modalMode === 'add'
        ? formData
        : { ...formData, id: currentKin.id };

      console.log('Submitting to:', url);
      console.log('Request body:', requestBody);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log('Submit result:', result);

      if (result.status) {
        if (modalMode === 'add') {
          // For add mode: Replace the temporary ID with the real ID from server
          setNextOfKins(prev => prev.map(kin =>
            kin.id === optimisticData.id
              ? {
                ...result.data, // Use the complete server response data
                id: result.data.id // Ensure ID is from server
              }
              : kin
          ));
        } else {
          // For edit mode: Update with server response data
          setNextOfKins(prev => prev.map(kin =>
            kin.id === currentKin.id
              ? {
                ...result.data, // Use the complete server response data
                id: currentKin.id // Keep the same ID
              }
              : kin
          ));
        }
      } else {
        // Revert optimistic update on error
        setError(result.message || 'Operation failed');
        await fetchNextOfKin(); // Refresh to correct state
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
      // Revert optimistic update on error
      await fetchNextOfKin();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setError(null);
    setSubmitting(true);

    // Store the deleted item for potential revert
    const deletedKin = nextOfKins.find(kin => kin.id === id);

    try {
      // Apply optimistic update immediately
      handleDeleteOptimistic(id);
      setDeleteConfirm(null);
      setSuccess('Next of kin deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);

      const response = await fetch(`${BASE_URL}/delete-next-of-kin?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });

      const result = await response.json();
      console.log('Delete result:', result);

      if (!result.status) {
        // Revert optimistic update on error
        setError(result.message || 'Delete failed');
        setNextOfKins(prev => [...prev, deletedKin]);
      }
      // On success, we keep our optimistic update (the item is already removed)
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
      // Revert optimistic update on error
      setNextOfKins(prev => [...prev, deletedKin]);
    } finally {
      setSubmitting(false);
    }
  };

  const getRelationshipIcon = (relationship) => {
    const icons = {
      'Spouse': 'bi-heart-fill',
      'Child': 'bi-person-badge',
      'Parent': 'bi-people-fill',
      'Sibling': 'bi-person-heart',
      'Friend': 'bi-emoji-smile',
      'Other': 'bi-person-circle'
    };
    return icons[relationship] || 'bi-person-circle';
  };

  const getRelationshipColor = (relationship) => {
    const colors = {
      'Spouse': 'danger',
      'Child': 'success',
      'Parent': 'warning',
      'Sibling': 'info',
      'Friend': 'primary',
      'Other': 'secondary'
    };
    return colors[relationship] || 'secondary';
  };

  if (loading && nextOfKins.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-danger mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted">Loading next of kin...</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Success Toast */}
      {success && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="toast show" role="alert">
            <div className="toast-header bg-success text-white">
              <i className="bi bi-check-circle-fill me-2"></i>
              <strong className="me-auto">Success</strong>
              <button type="button" className="btn-close btn-close-white" onClick={() => setSuccess(null)}></button>
            </div>
            <div className="toast-body">
              {success}
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="toast show" role="alert">
            <div className="toast-header bg-danger text-white">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong className="me-auto">Error</strong>
              <button type="button" className="btn-close btn-close-white" onClick={() => setError(null)}></button>
            </div>
            <div className="toast-body">
              {error}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">
                <i className="bi bi-heart text-danger me-2"></i>
                Next of Kin
              </h5>
              <p className="text-muted small mb-0">
                {nextOfKins.length > 0
                  ? 'You have one next of kin registered. You can edit or delete this record.'
                  : 'Add your next of kin information. You can only add one next of kin.'}
              </p>
            </div>
            {nextOfKins.length === 0 && (
              <button
                className="btn btn-outline-primary rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                onClick={openAddModal}
                style={{ borderWidth: '2px' }}
              >
                <i className="bi bi-plus-lg"></i>
                <span>Add Next of Kin</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4">
              <div className="modal-header border-0 pt-4">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => setDeleteConfirm(null)}></button>
              </div>
              <div className="modal-body text-center py-4">
                <div className="bg-danger bg-opacity-10 rounded-circle p-4 d-inline-block mb-3">
                  <i className="bi bi-trash text-danger" style={{ fontSize: '2rem' }}></i>
                </div>
                <p className="mb-2">
                  Are you sure you want to delete <strong className="text-danger">{deleteConfirm.full_name}</strong>?
                </p>
                <p className="text-muted small mb-0">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 pb-4">
                <button
                  type="button"
                  className="btn btn-light rounded-pill px-4"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-pill px-4"
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-2"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4">
              <div className="modal-header border-0 pt-4">
                <h5 className="modal-title fw-bold">
                  <i className={`bi ${modalMode === 'add' ? 'bi-plus-circle' : 'bi-pencil'} text-danger me-2`}></i>
                  {modalMode === 'add' ? 'Add Next of Kin' : 'Edit Next of Kin'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Relationship <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select relationship</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Parent">Parent</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control form-control-lg"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Address (Optional)</label>
                    <textarea
                      className="form-control"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter address"
                      rows="2"
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 pb-4">
                  <button
                    type="button"
                    className="btn btn-light rounded-pill px-4"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {modalMode === 'add' ? 'Adding...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${modalMode === 'add' ? 'bi-plus-lg' : 'bi-check-lg'} me-2`}></i>
                        {modalMode === 'add' ? 'Add Next of Kin' : 'Update'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Next of Kin Display */}
      {nextOfKins.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body text-center py-5">
            <div className="bg-danger bg-opacity-10 rounded-circle p-4 d-inline-block mb-4">
              <i className="bi bi-heart text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h4 className="fw-bold mb-3">No Next of Kin Added</h4>
            <p className="text-muted mb-4">You haven't added any next of kin yet. Click the button above to add your next of kin.</p>
          </div>
        </div>
      ) : (
        <div className="row">
          {nextOfKins.map(kin => {
            const relationshipColor = getRelationshipColor(kin.relationship);
            return (
              <div key={kin.id} className="col-12">
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body p-4">
                    <div className="row align-items-center">
                      {/* Icon and Basic Info */}
                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className={`bg-${relationshipColor} bg-opacity-10 rounded-circle p-3 me-3`}>
                            <i className={`bi ${getRelationshipIcon(kin.relationship)} text-${relationshipColor} fs-3`}></i>
                          </div>
                          <div>
                            <h5 className="fw-bold mb-1">{kin.full_name}</h5>
                            <span className={`badge bg-${relationshipColor} bg-opacity-10 text-${relationshipColor} rounded-pill px-3 py-1`}>
                              {kin.relationship}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="col-12 col-md-4 mt-3 mt-md-0">
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-telephone text-muted me-2"></i>
                            <a href={`tel:${kin.phone}`} className="text-decoration-none text-dark">
                              {kin.phone}
                            </a>
                          </div>
                          {kin.address && (
                            <div className="d-flex align-items-start">
                              <i className="bi bi-geo-alt text-muted me-2"></i>
                              <span className="text-muted">{kin.address}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-12 col-md-2 mt-3 mt-md-0">
                        <div className="d-flex gap-2 justify-content-md-end">
                          <button
                            className="btn btn-outline-primary rounded-pill btn-sm px-3"
                            onClick={() => openEditModal(kin)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-danger rounded-pill btn-sm px-3"
                            onClick={() => setDeleteConfirm(kin)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style jsx>{`
        .fade-in {
          animation: fadeIn 0.3s ease-in;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .form-control-lg, .form-select-lg {
          border-radius: 10px;
          padding: 0.75rem 1rem;
        }
        
        .btn {
          border-radius: 10px;
        }
        
        .rounded-4 {
          border-radius: 1rem !important;
        }
        
        .card {
          transition: none;
        }
        
        .card:hover {
          transform: none;
          box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
        }
        
        .badge {
          font-weight: 500;
        }
        
        .modal-content {
          border-radius: 1rem;
        }
        
        .toast {
          min-width: 250px;
        }
        
        @media (max-width: 768px) {
          .btn-lg {
            padding: 0.5rem 1rem;
            font-size: 0.95rem;
          }
          
          .fs-3 {
            font-size: 1.5rem !important;
          }
          
          .p-4 {
            padding: 1rem !important;
          }
          
          .bg-opacity-10.rounded-circle.p-3 {
            padding: 0.75rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NextOfKin;